import React from "react";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { describeDuplicate } from "./duplicateInfo";

const DuplicateDetails = ({ entries }) => (
  <Box sx={{ py: 0.5 }}>
    <Typography
      fontFamily={FontFamily.Poppins}
      fontWeight={fontWeight.W600}
      sx={{ fontSize: "0.75rem", mb: 0.75 }}
    >
      {entries.length === 1
        ? "Duplicate of another application"
        : `Duplicate of ${entries.length} other applications`}
    </Typography>

    {entries.map((entry, index) => (
      <Box key={`${entry.caeId || entry.name}-${entry.reason}`}>
        {index > 0 && <Divider sx={{ my: 0.75, borderColor: "rgba(255,255,255,0.2)" }} />}

        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
          {entry.name}
          {entry.caeId ? ` · ${entry.caeId}` : ""}
        </Typography>

        <Typography sx={{ fontSize: "0.72rem", opacity: 0.95 }}>
          Why: {entry.reasonLabel}
        </Typography>

        {entry.fields.length > 0 && (
          <Typography sx={{ fontSize: "0.68rem", opacity: 0.8 }}>
            Matched on: {entry.fields.join(", ")}
          </Typography>
        )}

        {entry.detectedOn && dayjs(entry.detectedOn).isValid() && (
          <Typography sx={{ fontSize: "0.65rem", opacity: 0.65 }}>
            Detected {dayjs(entry.detectedOn).format("DD MMM YYYY")}
          </Typography>
        )}
      </Box>
    ))}
  </Box>
);

/**
 * The red DUPLICATE chip, with the reason behind it on hover.
 *
 * `duplicateLogs` already arrives fully populated on the student list, so no
 * extra request is needed to say who the duplicate is of and why.
 */
export const DuplicateBadge = ({ row }) => {
  const entries = describeDuplicate(row);

  const chip = (
    <Box
      // Focusable so the reason is reachable without a mouse.
      tabIndex={0}
      aria-label={
        entries.length
          ? `Duplicate: ${entries
              .map((e) => `${e.name} ${e.reasonLabel}`)
              .join("; ")}`
          : "Duplicate"
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        px: 1.2,
        py: 0.3,
        borderRadius: "6px",
        backgroundColor: "#FEF2F2",
        border: "1px solid #FECACA",
        cursor: entries.length ? "help" : "default",
        outline: "none",
        "&:hover": entries.length ? { backgroundColor: "#FEE2E2" } : undefined,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: "#EF4444",
          flexShrink: 0,
        }}
      />
      <Typography
        fontFamily={FontFamily.Poppins}
        fontWeight={fontWeight.W600}
        sx={{
          fontSize: "0.65rem",
          color: "#DC2626",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          lineHeight: 1,
        }}
      >
        Duplicate
      </Typography>
    </Box>
  );

  // Nothing useful to say - the flag is set but no log survived. Better a plain
  // chip than an empty tooltip.
  if (!entries.length) return chip;

  return (
    <Tooltip
      arrow
      placement="right"
      title={<DuplicateDetails entries={entries} />}
      slotProps={{ tooltip: { sx: { maxWidth: 340 } } }}
    >
      {chip}
    </Tooltip>
  );
};

export default DuplicateBadge;
